import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Loader2, MessageCircle, User, Check, Star, DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

import { BACKEND_URL } from "../utils/backendUrl";
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  
  // Date filter state
  const [dateRange, setDateRange] = useState('30'); // days or 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?token=${token}`);
      setUsers(response.data.users);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("No tienes permisos de administrador");
      } else {
        setError("Error al cargar usuarios");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClickStats = async (days = 30, startDate = null, endDate = null) => {
    setStatsLoading(true);
    try {
      let params = `token=${token}`;
      if (startDate && endDate) {
        params += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        params += `&days=${days}`;
      }
      
      const [statsRes, detailedRes, alertsRes] = await Promise.all([
        axios.get(`${API}/admin/stats/clicks?${params}`),
        axios.get(`${API}/admin/stats/detailed?${params}`),
        axios.get(`${API}/admin/alerts?token=${token}`)
      ]);
      setClickStats(statsRes.data);
      setDetailedStats(detailedRes.data);
      setAlerts(alertsRes.data.alerts);
      setUnreadAlerts(alertsRes.data.unread_count);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('fetch stats failed:', err.message);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'custom') {
      fetchClickStats(parseInt(value));
    }
  };

  const applyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      fetchClickStats(null, customStartDate, customEndDate);
    }
  };

  const markAlertRead = async (alertId) => {
    try {
      await axios.put(`${API}/admin/alerts/${alertId}/read?token=${token}`);
      setAlerts(alerts.map(a => a.id === alertId ? {...a, read: true} : a));
      setUnreadAlerts(prev => Math.max(0, prev - 1));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('markAlertRead failed:', err.message);
    }
  };

  const markAllAlertsRead = async () => {
    try {
      await axios.put(`${API}/admin/alerts/read-all?token=${token}`);
      setAlerts(alerts.map(a => ({...a, read: true})));
      setUnreadAlerts(0);
      toast.success("Todas las alertas marcadas como leídas");
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('markAllAlertsRead failed:', err.message);
    }
  };

  const fetchMonthly = async () => {
    setMonthlyLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats/monthly?token=${token}&months=12`);
      setMonthlyStats(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('fetchMonthly failed:', err.message);
    } finally {
      setMonthlyLoading(false);
    }
  };

  const sendMonthlySummary = async () => {
    if (!window.confirm("¿Enviar el resumen mensual ahora a gemalfernandez@gmail.com? Se enviará con datos del mes anterior.")) return;
    try {
      toast.loading("Enviando email...");
      const res = await axios.post(`${API}/admin/send-monthly-summary?token=${token}`);
      toast.dismiss();
      toast.success(`Email enviado: ${res.data.subject}`);
    } catch (err) {
      toast.dismiss();
      toast.error("Error al enviar email: " + (err.response?.data?.detail || err.message));
    }
  };

  const exportCSV = async (type) => {
    try {
      const endpoint = type === 'clicks' ? 'export/clicks' : 'export/materials-stats';
      let params = `token=${token}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      } else {
        params += `&days=${dateRange}`;
      }
      window.open(`${API}/admin/${endpoint}?${params}`, '_blank');
      toast.success("Descargando CSV...");
    } catch (err) {
      toast.error("Error al exportar");
    }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate('/');
      toast.error("Debes iniciar sesión para acceder a esta página");
      return;
    }

    if (!isAdmin) {
      navigate('/');
      toast.error("Solo los administradores pueden acceder a esta página");
      return;
    }

    fetchUsers();
    fetchClickStats(30);
  }, [user, token, navigate, isAdmin]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}&token=${token}`);
      toast.success(`Rol actualizado a ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar rol");
    }
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center">
        <Card className="p-8 text-center">
          <CardTitle className="mb-4">Acceso Restringido</CardTitle>
          <CardDescription>Solo los administradores pueden ver esta página</CardDescription>
          <Button className="mt-4" onClick={() => navigate('/')}>Volver al inicio</Button>
        </Card>
      </div>
    );
  }

  // Calculate max for chart scaling
  const maxDailyClicks = clickStats?.clicks_by_day?.length > 0 
    ? Math.max(...clickStats.clicks_by_day.map(d => d.count), 1) 
    : 1;

  return (
    <div className="min-h-screen pt-24 px-6" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Panel de Administración</h1>
          <p className="text-muted-foreground mt-2">Gestión de usuarios y estadísticas</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-6 border-b border-border">
          <button
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'users' ? 'text-[#1a8ccc] border-b-2 border-[#1a8ccc]' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('users')}
          >
            Usuarios
          </button>
          <button
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'stats' ? 'text-[#1a8ccc] border-b-2 border-[#1a8ccc]' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('stats')}
          >
            Estadísticas
          </button>
          <button
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'detailed' ? 'text-[#1a8ccc] border-b-2 border-[#1a8ccc]' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('detailed')}
          >
            Análisis Detallado
          </button>
          <button
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'monthly' ? 'text-[#1a8ccc] border-b-2 border-[#1a8ccc]' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => { setActiveTab('monthly'); if (!monthlyStats) fetchMonthly(); }}
            data-testid="tab-monthly"
          >
            Comparativas Mensuales
          </button>
          <button
            className={`pb-3 px-1 font-medium transition-colors relative ${activeTab === 'alerts' ? 'text-[#1a8ccc] border-b-2 border-[#1a8ccc]' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('alerts')}
          >
            Alertas
            {unreadAlerts > 0 && (
              <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadAlerts}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Usuarios Registrados ({users.length})
              </CardTitle>
              <CardDescription>Lista completa de usuarios con cuenta en la plataforma</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-destructive">{error}</div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No hay usuarios registrados</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium">Email</th>
                        <th className="text-left py-3 px-4 font-medium">Nombre</th>
                        <th className="text-left py-3 px-4 font-medium">Rol</th>
                        <th className="text-left py-3 px-4 font-medium">Fecha de Registro</th>
                        <th className="text-left py-3 px-4 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="py-3 px-4">{u.email}</td>
                          <td className="py-3 px-4">{u.name || '-'}</td>
                          <td className="py-3 px-4">
                            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                              {u.role === 'admin' ? 'Admin' : 'Usuario'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : '-'}
                          </td>
                          <td className="py-3 px-4">
                            {u.id !== user.id && u.email !== 'gemalfernandez@gmail.com' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRoleChange(u.id, u.role === 'admin' ? 'user' : 'admin')}
                              >
                                {u.role === 'admin' ? 'Quitar Admin' : 'Hacer Admin'}
                              </Button>
                            )}
                            {u.id === user.id && (
                              <span className="text-xs text-muted-foreground">(Tú)</span>
                            )}
                            {u.email === 'gemalfernandez@gmail.com' && u.id !== user.id && (
                              <span className="text-xs text-muted-foreground">(Admin Principal)</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Date Filter */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Periodo:</span>
                    <select
                      value={dateRange}
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                      className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="7">Última semana</option>
                      <option value="30">Último mes</option>
                      <option value="90">Últimos 3 meses</option>
                      <option value="180">Últimos 6 meses</option>
                      <option value="365">Último año</option>
                      <option value="custom">Personalizado</option>
                    </select>
                  </div>
                  {dateRange === 'custom' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                      />
                      <span className="text-muted-foreground">a</span>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="px-3 py-2 rounded-md border border-input bg-background text-sm"
                      />
                      <Button size="sm" onClick={applyCustomDateRange}>
                        Aplicar
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#1a8ccc]">{clickStats?.total_clicks || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Clicks Totales</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#00a651]">{clickStats?.clicks_by_destination?.bambulab || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Clicks BambuLab</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-[#1575b0]">{clickStats?.clicks_by_destination?.sicnova || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Clicks Sicnova</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-orange-500">{clickStats?.clicks_by_destination?.formlabs || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Clicks Formlabs</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Clicks Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Clicks por Día (Últimos 30 días)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : clickStats?.clicks_by_day?.length > 0 ? (
                  <div className="h-48 flex items-end gap-1">
                    {clickStats.clicks_by_day.map((day, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-[#1a8ccc] rounded-t transition-all hover:bg-[#1575b0]"
                          style={{ height: `${(day.count / maxDailyClicks) * 150}px`, minHeight: day.count > 0 ? '4px' : '0' }}
                          title={`${day.date}: ${day.count} clicks`}
                        />
                        {i % 5 === 0 && (
                          <span className="text-[10px] text-muted-foreground rotate-45 origin-left">
                            {day.date.slice(5)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">No hay datos de clicks aún</div>
                )}
              </CardContent>
            </Card>

            {/* Top Materials */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Top 10 Materiales Más Clickeados
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : clickStats?.top_materials?.length > 0 ? (
                  <div className="space-y-3">
                    {clickStats.top_materials.map((mat, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <span className="w-6 h-6 rounded-full bg-[#1a8ccc] text-white text-sm flex items-center justify-center font-medium">
                          {i + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{mat.material_name}</span>
                            <span className="text-sm text-muted-foreground">{mat.count} clicks</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#1a8ccc] rounded-full"
                              style={{ width: `${(mat.count / (clickStats.top_materials[0]?.count || 1)) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">No hay datos de clicks aún</div>
                )}
              </CardContent>
            </Card>

            {/* Recent Clicks */}
            <Card>
              <CardHeader>
                <CardTitle>Últimos 20 Clicks</CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : clickStats?.recent_clicks?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium">Material</th>
                          <th className="text-left py-2 px-3 font-medium">Destino</th>
                          <th className="text-left py-2 px-3 font-medium">Fecha/Hora</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clickStats.recent_clicks.map((click, i) => (
                          <tr key={i} className="border-b hover:bg-muted/50">
                            <td className="py-2 px-3">{click.material_name}</td>
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="capitalize">{click.destination}</Badge>
                            </td>
                            <td className="py-2 px-3 text-muted-foreground">
                              {new Date(click.timestamp).toLocaleString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">No hay clicks registrados aún</div>
                )}
              </CardContent>
            </Card>

            {/* Export Buttons */}
            <Card>
              <CardHeader>
                <CardTitle>Exportar Datos</CardTitle>
                <CardDescription>Descarga las estadísticas en formato CSV</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <Button onClick={() => exportCSV('clicks')} variant="outline">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Exportar Clicks (CSV)
                  </Button>
                  <Button onClick={() => exportCSV('materials')} variant="outline">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Exportar Stats por Material (CSV)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Analysis Tab */}
        {activeTab === 'detailed' && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Growth & Comparison */}
                <div className="grid md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-[#1a8ccc]">{detailedStats?.current_period_clicks || 0}</p>
                        <p className="text-sm text-muted-foreground mt-1">Clicks (30 días)</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className={`text-4xl font-bold ${(detailedStats?.growth_percentage || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {(detailedStats?.growth_percentage || 0) >= 0 ? '+' : ''}{detailedStats?.growth_percentage || 0}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Crecimiento vs anterior</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-[#00a651]">{detailedStats?.avg_clicks_per_day || 0}</p>
                        <p className="text-sm text-muted-foreground mt-1">Media diaria</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-orange-500">{detailedStats?.unique_materials_clicked || 0}</p>
                        <p className="text-sm text-muted-foreground mt-1">Materiales distintos</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Clicks by Hour */}
                <Card>
                  <CardHeader>
                    <CardTitle>Distribución por Hora del Día</CardTitle>
                    <CardDescription>¿A qué hora hacen más clicks tus usuarios?</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {detailedStats?.clicks_by_hour?.length > 0 ? (
                      <div className="h-32 flex items-end gap-1">
                        {Array.from({length: 24}, (_, i) => {
                          const hourData = detailedStats.clicks_by_hour.find(h => parseInt(h.hour) === i);
                          const count = hourData?.count || 0;
                          const maxHour = Math.max(...detailedStats.clicks_by_hour.map(h => h.count), 1);
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <div 
                                className="w-full bg-[#1a8ccc] rounded-t transition-all hover:bg-[#1575b0]"
                                style={{ height: `${(count / maxHour) * 100}px`, minHeight: count > 0 ? '4px' : '0' }}
                                title={`${i}:00 - ${count} clicks`}
                              />
                              {i % 4 === 0 && <span className="text-[10px] text-muted-foreground">{i}h</span>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">No hay datos suficientes</div>
                    )}
                  </CardContent>
                </Card>

                {/* Category Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>Clicks por Categoría</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-8 justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 rounded-full bg-[#1a8ccc] flex items-center justify-center text-white text-2xl font-bold">
                          {detailedStats?.clicks_by_category?.filament || 0}
                        </div>
                        <p className="mt-2 text-sm font-medium">Filamentos</p>
                      </div>
                      <div className="text-center">
                        <div className="w-24 h-24 rounded-full bg-[#00a651] flex items-center justify-center text-white text-2xl font-bold">
                          {detailedStats?.clicks_by_category?.resin || 0}
                        </div>
                        <p className="mt-2 text-sm font-medium">Resinas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Period Comparison */}
                <Card>
                  <CardHeader>
                    <CardTitle>Comparativa de Periodos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-8">
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground mb-2">Periodo anterior (30 días)</p>
                        <div className="h-8 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gray-400 rounded-full"
                            style={{ width: `${Math.min(100, (detailedStats?.previous_period_clicks || 0) / Math.max(detailedStats?.current_period_clicks || 1, 1) * 100)}%` }}
                          />
                        </div>
                        <p className="text-right mt-1 font-medium">{detailedStats?.previous_period_clicks || 0} clicks</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground mb-2">Periodo actual (30 días)</p>
                        <div className="h-8 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#1a8ccc] rounded-full"
                            style={{ width: '100%' }}
                          />
                        </div>
                        <p className="text-right mt-1 font-medium">{detailedStats?.current_period_clicks || 0} clicks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Monthly Comparison Tab */}
        {activeTab === 'monthly' && (
          <div className="space-y-6" data-testid="monthly-tab-content">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Comparativas Mensuales
                    </CardTitle>
                    <CardDescription>Evolución de clicks por marca a lo largo de los últimos 12 meses</CardDescription>
                  </div>
                  <Button onClick={fetchMonthly} variant="outline" size="sm" disabled={monthlyLoading}>
                    {monthlyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
                  </Button>
                  <Button
                    onClick={sendMonthlySummary}
                    size="sm"
                    className="ml-2 bg-[#00a651] hover:bg-[#008c45] text-white"
                    data-testid="send-monthly-summary-btn"
                  >
                    Enviar resumen por email
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {monthlyLoading || !monthlyStats ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* MoM Delta cards */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                      {[
                        { key: 'total', label: 'Total', color: '#1a8ccc' },
                        { key: 'esun', label: 'eSUN', color: '#00a651' },
                        { key: 'bambulab', label: 'Bambu Lab', color: '#1a8ccc' },
                        { key: 'formlabs', label: 'Formlabs', color: '#7e57c2' },
                        { key: 'sicnova', label: 'SICNOVA', color: '#f59e0b' },
                      ].map(({ key, label, color }) => {
                        const d = monthlyStats.deltas_vs_previous_month?.[key] || { prev: 0, current: 0, delta_pct: null };
                        const positive = d.delta_pct !== null && d.delta_pct >= 0;
                        return (
                          <div key={key} className="p-4 border border-border rounded-sm bg-card" data-testid={`monthly-delta-${key}`}>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2" style={{ color }}>{label}</div>
                            <div className="text-2xl font-mono font-bold">{d.current}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              vs mes anterior ({d.prev})
                              {d.delta_pct !== null && (
                                <span className={`ml-1 font-medium ${positive ? 'text-green-600' : 'text-red-500'}`}>
                                  {positive ? '↑' : '↓'} {Math.abs(d.delta_pct)}%
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Stacked bar chart by month */}
                    <div>
                      <h4 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
                        Clicks por mes (apilado por destino)
                      </h4>
                      {(() => {
                        const months = monthlyStats.months || [];
                        const maxTotal = Math.max(1, ...months.map((m) => m.total));
                        const palette = { esun: '#00a651', bambulab: '#1a8ccc', formlabs: '#7e57c2', sicnova: '#f59e0b' };
                        const fmtMonth = (s) => {
                          const [y, m] = s.split('-');
                          const names = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                          return `${names[parseInt(m,10)-1]} ${y.slice(2)}`;
                        };
                        return (
                          <div className="grid grid-cols-12 gap-2 items-end h-64" data-testid="monthly-chart">
                            {months.map((m) => {
                              const heightPct = (m.total / maxTotal) * 100;
                              return (
                                <div key={m.month} className="flex flex-col items-center justify-end h-full">
                                  <div className="text-xs font-mono mb-1">{m.total > 0 ? m.total : ''}</div>
                                  <div className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ height: `${heightPct}%`, minHeight: m.total > 0 ? '8px' : '2px', backgroundColor: m.total === 0 ? '#e5e7eb' : 'transparent' }}>
                                    {monthlyStats.destinations.map((d) => {
                                      if (m[d] === 0) return null;
                                      const segPct = (m[d] / m.total) * 100;
                                      return (
                                        <div
                                          key={d}
                                          style={{ height: `${segPct}%`, backgroundColor: palette[d] }}
                                          title={`${d}: ${m[d]}`}
                                        />
                                      );
                                    })}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-2 font-mono whitespace-nowrap rotate-0 sm:rotate-0">
                                    {fmtMonth(m.month)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* Legend */}
                      <div className="flex flex-wrap gap-4 mt-6 text-xs">
                        {[
                          { key: 'esun', label: 'eSUN', color: '#00a651' },
                          { key: 'bambulab', label: 'Bambu Lab', color: '#1a8ccc' },
                          { key: 'formlabs', label: 'Formlabs', color: '#7e57c2' },
                          { key: 'sicnova', label: 'SICNOVA', color: '#f59e0b' },
                        ].map(({ key, label, color }) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                            <span className="font-mono text-muted-foreground">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5" />
                      Alertas de Hitos
                    </CardTitle>
                    <CardDescription>Notificaciones cuando un material alcanza 100, 200, 300... clicks</CardDescription>
                  </div>
                  {unreadAlerts > 0 && (
                    <Button variant="outline" size="sm" onClick={markAllAlertsRead}>
                      Marcar todas como leídas
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay alertas aún</p>
                    <p className="text-sm mt-1">Recibirás una alerta cada vez que un material alcance 100 clicks</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div 
                        key={alert.id} 
                        className={`p-4 rounded-lg border ${alert.read ? 'bg-background border-border' : 'bg-[#1a8ccc]/10 border-[#1a8ccc]'}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Star className={`w-5 h-5 ${alert.read ? 'text-muted-foreground' : 'text-[#1a8ccc]'}`} />
                              <span className={`font-medium ${alert.read ? '' : 'text-[#1a8ccc]'}`}>
                                ¡Hito alcanzado!
                              </span>
                              {!alert.read && (
                                <Badge className="bg-[#1a8ccc]">Nueva</Badge>
                              )}
                            </div>
                            <p className="mt-1">{alert.message}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {new Date(alert.timestamp).toLocaleString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          {!alert.read && (
                            <Button variant="ghost" size="sm" onClick={() => markAlertRead(alert.id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};


export default AdminPage;
